// Authentication service for login, logout, and token management
import { API_URL, TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY, MEMBERSHIP_KEY, USE_MOCK_AUTH } from '../config';

// Save authentication tokens and user data to localStorage
export const saveAuth = (tokens, userData, membershipData = null) => {
  localStorage.setItem(TOKEN_KEY, tokens.access);
  localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh);
  localStorage.setItem(USER_KEY, JSON.stringify(userData));
  if (membershipData) {
    localStorage.setItem(MEMBERSHIP_KEY, JSON.stringify(membershipData));
  } else {
    localStorage.removeItem(MEMBERSHIP_KEY);
  }
};

// Get the current authentication token
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

// Get the refresh token
export const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

// Get the current user data from localStorage
export const getLocalUser = () => {
  const userString = localStorage.getItem(USER_KEY);
  return userString ? JSON.parse(userString) : null;
};

// Clear all authentication data from localStorage
export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(MEMBERSHIP_KEY);
};

// Check if the user is currently authenticated
export const isAuthenticated = () => {
  return !!getToken();
};

// Attempt to refresh the authentication token
export const refreshToken = async () => {
  try {
    const refresh = getRefreshToken();
    if (!refresh) throw new Error('No refresh token available');
    
    const response = await fetch(`${API_URL}/auth/token/refresh/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh }),
    });
    
    if (!response.ok) throw new Error('Token refresh failed');
    
    const tokens = await response.json();
    localStorage.setItem(TOKEN_KEY, tokens.access);
    return tokens.access;
  } catch (error) {
    clearAuth();
    throw error;
  }
};

// Login user with email and password
export const login = async (email, password) => {
  try {
    console.log('Attempting login with:', email);
    console.log('Using API URL:', API_URL);
    
    // Check if API_URL is available
    if (!API_URL) {
      console.error('API URL is not configured properly');
      throw new Error('API URL is not configured properly. Please contact support.');
    }
    
    // Try the actual API endpoint first
    try {
      console.log(`Attempting login at: ${API_URL}/auth/token/`);
      
      const response = await fetch(`${API_URL}/auth/token/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });
      
      console.log('Login API response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        
        // JWT endpoint returns {access: "token", refresh: "token"} format
        // We need to fetch user data separately
        if (data.access) {
          try {
            const userResponse = await fetch(`${API_URL}/auth/user/`, {
              headers: {
                'Authorization': `Bearer ${data.access}`,
                'Content-Type': 'application/json',
              },
            });
            
            if (userResponse.ok) {
              const userData = await userResponse.json();
              saveAuth(data, userData);
              return userData;
            } else {
              console.error('Failed to fetch user data:', userResponse.status);
              throw new Error('Failed to fetch user information');
            }
          } catch (userError) {
            console.error('Error fetching user data:', userError);
            throw new Error('Failed to fetch user information');
          }
        }
        
        // Fallback for unexpected response format
        saveAuth(data, data.user);
        console.log('🔄 Returning user data:', data.user);
        return data.user;
      }
      
      // Try to extract meaningful error message from response
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.message || errorData.error;
        
        // Handle nested error objects
        if (!errorMessage && typeof errorData === 'object') {
          const messages = [];
          Object.entries(errorData).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              messages.push(`${key}: ${value.join(' ')}`);
            } else if (typeof value === 'string') {
              messages.push(`${key}: ${value}`);
            }
          });
          errorMessage = messages.join('. ');
        }
      } catch (e) {
        errorMessage = response.statusText || `Server error (${response.status})`;
      }
      
      console.error('Login API error:', response.status, errorMessage);
      
      // Only fall back to mock auth if configured to do so and API fails
      if (USE_MOCK_AUTH) {
        console.log('API login failed, falling back to mock authentication');
        return performMockLogin(email, password);
      }
      
      throw new Error(`Login failed: ${errorMessage}`);
    } catch (apiError) {
      console.error('Login API exception:', apiError);
      
      // Only fall back to mock auth if configured to do so and API call fails
      if (USE_MOCK_AUTH) {
        console.log('API call failed, falling back to mock authentication');
        return performMockLogin(email, password);
      }
      
      throw new Error(`Connection error: ${apiError.message || 'Could not connect to authentication server'}`);
    }
  } catch (error) {
    console.error('Login complete failure:', error);
    throw error;
  }
};

// Helper function for mock login
const performMockLogin = (email, password) => {
  console.log('Using mock authentication for login');
  
  // Check if there's a locally registered mock user with this email
  const localUsers = JSON.parse(localStorage.getItem('mockUsers') || '{}');

  // If this is a mock registered user or default admin user
  if (localUsers[email]?.password === password || (email === 'admin@example.com' && password === 'admin123')) {
    // Create mock tokens and user data
    const mockData = {
      access: 'mock_access_token_' + Date.now(),
      refresh: 'mock_refresh_token_' + Date.now(),
      user: {
        id: 'mock_' + Date.now(),
        email: email,
        first_name: localUsers[email]?.firstName || 'Admin',
        last_name: localUsers[email]?.lastName || 'User',
        is_staff: email === 'admin@example.com'
      }
    };
    
    // Save mock authentication data
    console.log('🎭 Mock login successful, returning user data:', mockData.user);
    saveAuth(mockData, mockData.user);
    return mockData.user;
  } else {
    throw new Error('Invalid email or password');
  }
};

// Register a new user
export const register = async (userData) => {
  try {
    console.log('Attempting registration with:', JSON.stringify(userData));
    console.log('Using API URL:', API_URL);
    
    // Check if API_URL is available
    if (!API_URL) {
      console.error('API URL is not configured properly');
      throw new Error('API URL is not configured properly. Please contact support.');
    }
    
    // Try the actual API endpoint first
    try {
      console.log(`Attempting registration at: ${API_URL}/auth/registration/`);
      
      const response = await fetch(`${API_URL}/auth/registration/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
      
      console.log('Registration API response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Registration successful');
        saveAuth(data, data.user, data.membership);
        return {
          user: data.user,
          membership: data.membership
        };
      }
      
      // Try to extract meaningful error message from response
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.message || errorData.error;
        
        // Handle nested error objects
        if (!errorMessage && typeof errorData === 'object') {
          const messages = [];
          Object.entries(errorData).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              messages.push(`${key}: ${value.join(' ')}`);
            } else if (typeof value === 'string') {
              messages.push(`${key}: ${value}`);
            }
          });
          errorMessage = messages.join('. ');
        }
      } catch (e) {
        errorMessage = response.statusText || `Server error (${response.status})`;
      }
      
      console.error('Registration API error:', response.status, errorMessage);
      
      // Only fall back to mock auth if configured to do so and API fails
      if (USE_MOCK_AUTH) {
        console.log('API registration failed, falling back to mock registration');
        const mockUser = performMockRegister(userData);
        return {
          user: mockUser,
          membership: null
        };
      }
      
      throw new Error(`Registration failed: ${errorMessage}`);
    } catch (apiError) {
      console.error('Registration API exception:', apiError);
      
      // Only fall back to mock auth if configured to do so and API call fails
      if (USE_MOCK_AUTH) {
        console.log('API call failed, falling back to mock registration');
        return performMockRegister(userData);
      }
      
      throw new Error(`Connection error: ${apiError.message || 'Could not connect to registration server'}`);
    }
  } catch (error) {
    console.error('Registration complete failure:', error);
    throw error;
  }
};

// Helper function for mock registration
const performMockRegister = (userData) => {
  console.log('Using mock authentication for registration');
  
  // Store mock user in localStorage for later login
  const localUsers = JSON.parse(localStorage.getItem('mockUsers') || '{}');
  
  // Check if user already exists
  if (localUsers[userData.email]) {
    throw new Error('User with this email already exists');
  }
  
  localUsers[userData.email] = {
    firstName: userData.first_name || '',
    lastName: userData.last_name || '',
    password: userData.password  // Normally we'd never store passwords like this
  };
  localStorage.setItem('mockUsers', JSON.stringify(localUsers));
  
  // Create mock tokens and user data
  const mockData = {
    access: 'mock_access_token_' + Date.now(),
    refresh: 'mock_refresh_token_' + Date.now(),
    user: {
      id: 'mock_' + Date.now(),
      email: userData.email,
      first_name: userData.first_name || '',
      last_name: userData.last_name || '',
      is_staff: false
    }
  };
  
  // Save mock authentication data
  saveAuth(mockData, mockData.user);
  return mockData.user;
};

// Logout the current user
export const logout = async () => {
  try {
    const token = getToken();
    const refresh = getRefreshToken();
    if (token && API_URL && !USE_MOCK_AUTH) {
      try {
        await fetch(`${API_URL}/auth/logout/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh }),
        });
      } catch (error) {
        console.log('API logout failed, but proceeding with client-side logout');
      }
    }
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    clearAuth();
  }
};

// Handle Google OAuth callback
export const googleCallback = async (code) => {
  try {
    console.log('Processing Google OAuth callback with code');
    
    const redirectUri = `${window.location.origin}/auth/google/callback`;
    
    const response = await fetch(`${API_URL}/auth/google/callback/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        code,
        redirect_uri: redirectUri
      }),
    });
    
    console.log('Google callback API response status:', response.status);
    
    if (response.ok) {
      const data = await response.json();
      
      // The response should contain JWT tokens and user data
      if (data.access) {
        // Save auth data
        const authData = {
          access: data.access,
          refresh: data.refresh
        };
        
        // Get user and membership data
        const userData = data.user || {};
        const membershipData = data.membership || null;
        saveAuth(authData, userData, membershipData);
        
        // If user data is not included, fetch it
        if (!userData.email) {
          try {
            const userResponse = await fetch(`${API_URL}/auth/user/`, {
              headers: {
                'Authorization': `Bearer ${data.access}`,
                'Content-Type': 'application/json',
              },
            });
            
            if (userResponse.ok) {
              const fullUserData = await userResponse.json();
              localStorage.setItem(USER_KEY, JSON.stringify(fullUserData));
              return {
                user: fullUserData,
                membership: membershipData
              };
            }
          } catch (userError) {
            console.error('Error fetching user data after Google login:', userError);
          }
        }

        return {
          user: userData,
          membership: membershipData
        };
      }
      
      throw new Error('Invalid response from Google authentication');
    }
    
    // Handle error response
    let errorMessage;
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.detail || 'Google authentication failed';
    } catch (e) {
      errorMessage = 'Google authentication failed';
    }
    
    throw new Error(errorMessage);
    
  } catch (error) {
    console.error('Google OAuth callback error:', error);
    throw error;
  }
};

// Get the current user's information from the API
export const getUser = async () => {
  try {
    console.log('👤 getUser called, checking authentication...');
    if (!isAuthenticated()) {
      console.log('❌ Not authenticated, returning null');
      return null;
    }
    
    // If using mock authentication or no API URL available, return local user data
    if (USE_MOCK_AUTH || !API_URL) {
      console.log('🎭 Using mock auth or no API URL, getting local user');
      const localUser = getLocalUser();
      console.log('📱 Local user data:', localUser);
      return localUser;
    }
    
    const token = getToken();
    try {
      const response = await fetch(`${API_URL}/auth/user/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          try {
            // Attempt to refresh the token
            const newToken = await refreshToken();
            const retryResponse = await fetch(`${API_URL}/auth/user/`, {
              headers: {
                'Authorization': `Bearer ${newToken}`,
              },
            });
            
            if (retryResponse.ok) {
              const userData = await retryResponse.json();
              localStorage.setItem(USER_KEY, JSON.stringify(userData));
              return userData;
            }
            
            throw new Error('Authentication failed after token refresh');
          } catch (refreshError) {
            // If token refresh fails, fall back to local user data if available
            const localUser = getLocalUser();
            if (localUser) {
              console.log('Falling back to local user data after failed token refresh');
              return localUser;
            }
            clearAuth();
            throw refreshError;
          }
        }
        
        // If response is not ok for other reasons, fall back to local user data if available
        const localUser = getLocalUser();
        if (localUser) {
          console.log('Falling back to local user data after API error');
          return localUser;
        }
        throw new Error('Failed to get user information');
      }
      
      const userData = await response.json();
      localStorage.setItem(USER_KEY, JSON.stringify(userData));
      return userData;
    } catch (error) {
      // If API call fails entirely, fall back to local user data if available
      const localUser = getLocalUser();
      if (localUser) {
        console.log('Falling back to local user data after API exception');
        return localUser;
      }
      clearAuth();
      throw error;
    }
  } catch (error) {
    clearAuth();
    throw error;
  }
};
